import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { format, addDays, subDays, isToday as isTodayFn } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from '@/lib/utils';

interface DateNavigationProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  className?: string;
  showRelativeDay?: boolean;
}

export function DateNavigation({ 
  selectedDate, 
  onDateChange, 
  className,
  showRelativeDay = true 
}: DateNavigationProps) {
  const isToday = isTodayFn(selectedDate);
  
  const getRelativeDay = () => {
    if (isToday) return 'Today';
    const diffTime = new Date().getTime() - selectedDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays > 1 && diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays > 7 && diffDays <= 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays > 30) return `${Math.floor(diffDays / 30)} months ago`;
    return format(selectedDate, 'MMM d, yyyy');
  };

  return (
    <div className={cn("flex items-center justify-between gap-4", className)}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDateChange(subDays(selectedDate, 1))}
        className="h-9 w-9"
      >
        <ChevronLeft className="h-5 w-5" />
      </Button>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "min-w-[200px] justify-center font-medium",
              isToday && "border-primary"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            <div className="flex flex-col items-start">
              <span className="text-sm">{format(selectedDate, 'EEEE, MMM d')}</span>
              {showRelativeDay && (
                <span className="text-xs text-muted-foreground">{getRelativeDay()}</span>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && onDateChange(date)}
            initialFocus
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>

      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDateChange(addDays(selectedDate, 1))}
        disabled={isToday}
        className="h-9 w-9"
      >
        <ChevronRight className="h-5 w-5" />
      </Button>

      {!isToday && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDateChange(new Date())}
          className="ml-2"
        >
          Today
        </Button>
      )}
    </div>
  );
}
